import {
  HomeworkItem,
  HomeworkPlan,
  HomeworkContent,
  HomeworkPlanForm,
  HomeworkRepeatBy,
  HomeworkTemplate,
  CompetitionScopeClazz,
  IHomeworkIntelligentRule,
} from 'kuaidong-model'

import cloneDeep from 'lodash/cloneDeep'
import { AClazz, ACompetition } from 'kuaidong-api'
import startOfDay from 'date-fns/startOfDay'
import { globalConfigStore } from 'kuaidong-store'
import { DateWrapper, Weekday, DateUtil } from 'kuaidong-lib'
import { AHomeworkNew } from 'kuaidong-api/src/AHomeworkNew'
import * as homeworkPlanService from './HomeworkPlanService'
import { HomeworkTemplateItem, Clazz } from 'kuaidong-model'
import { inferRepeatBy, calcRepeatBy } from './HomeworkPlanService'

import isToday from 'date-fns/isToday'

let editingForm: HomeworkPlanForm | null = null

export function setEditingForm(form: HomeworkPlanForm | null) {
  editingForm = form
}

export function getRecentExpireTimeByClassList(list: Clazz[]): string {
  if (list.length == 0) {
    return ''
  }
  let res = list[0].expire_time || ''
  for (let i = 1; i < list.length; i++) {
    if (list[i].expire_time) {
      if (!res || DateUtil.isPast(new Date(list[i].expire_time || new Date()), res)) {
        res = list[i].expire_time || ''
      }
    }
  }
  return res
}

export function initForm(): HomeworkPlanForm {
  return {
    groupId: null,
    classes: [],
    homeworkContents: [{ homework_items: [] }],
    startDate: new DateWrapper(startOfDay(new Date())).showFullStr,
    endDate: null,
    repeatBy: 'day',
    fridayChecked: false,
    completion_start_time: null,
    completion_end_time: null,
    message: '同学们开始作业前记得做好热身准备活动哦~期待通过一次次练习可以看到你们更好的表现，加油！',
    releaseType: 'normal',
    publishToAllClasses: false,
    template_id: null,
    is_modify: null,
    is_clear_today: false,
  }
}

export function getEditingForm() {
  return editingForm || initForm()
}

export function clearEditingForm() {
  editingForm = null
}

export function newHomeworkContent(day: Weekday | null) {
  return {
    homework_items: [],
    ...(day ? { day_of_week: day } : {}),
  }
}

export function buildHomeworkContentsFromTemplate(homeworkContents: HomeworkContent[]): HomeworkContent[] {
  return homeworkContents.map((c) => ({
    ...c,
    homework_items: c.homework_items.map((i) => ({
      ...i,
      gender_scope: i.gender_scope || 'ALL',
    })),
  }))
}

function buildHomeworkItemsFromTemplate(templateItems: HomeworkTemplateItem[]): HomeworkItem[] {
  return templateItems.map((item) => ({
    drill_id: item.drill.drill_id,
    repeats: item.drill.default,
    container_type: item.name,
    homework_desc: item.title,
    gender_scope: 'ALL',
  }))
}

async function buildDefaultHomeworkItems(ownClasses: CompetitionScopeClazz[]): Promise<HomeworkItem[]> {
  if (!ownClasses.length) {
    return []
  }

  await globalConfigStore.ensureHomeworkIsLoaded()

  const templateItems = globalConfigStore.findDefaultTemplateItems()

  return buildHomeworkItemsFromTemplate(templateItems)
}

async function fetchOwnClasses(): Promise<CompetitionScopeClazz[]> {
  const config = await ACompetition.fetchCompetitionScopeConfig()
  return config.own_simple_classes
}

function filterOwnClassesByClazzIds(clazzes: CompetitionScopeClazz[], clazzIds: number[]): CompetitionScopeClazz[] {
  return clazzIds.map((classId) => clazzes.find((c) => c.id === classId)).filter(Boolean) as Clazz[]
}

export function validateClasses(form: HomeworkPlanForm | IHomeworkIntelligentRule) {
  if (!form.classes.length) {
    throw new Error('请选择班级')
  }
}

export function validateDate(form: HomeworkPlanForm | IHomeworkIntelligentRule) {
  if (!form.startDate) {
    throw new Error('请选择开始时间')
  }
}

export function validateHomeworkContentsRestItems(form: HomeworkPlanForm) {
  if (form.homeworkContents.length) {
    for (let i = 0; i < form.homeworkContents.length; i++) {
      if (form.homeworkContents[i].homework_items[0]?.container_type === 'rest') {
        throw new Error(`作业计划的首个项目无法设置为休息项目，请移除该休息项目～`)
      }
    }
  }
}

export function validateHomeworkContents(form: HomeworkPlanForm) {
  if (!(form.homeworkContents.length && form.homeworkContents.some((c) => c.homework_items.length > 0))) {
    throw new Error('请添加锻炼项目')
  }
  validateHomeworkContentsRestItems(form)
}

function defaultEditingDayByRepeatBy(repeatBy: HomeworkRepeatBy): Weekday | null {
  if (repeatBy === 'day') {
    return null
  } else if (repeatBy === 'weekend') {
    return 6
  } else {
    return 1
  }
}

export function defaultEditingDay(form: HomeworkPlanForm) {
  return form.repeatBy ? defaultEditingDayByRepeatBy(form.repeatBy) : null
}

export function defaultHomeworkContents(repeatBy: HomeworkRepeatBy): HomeworkContent[] {
  return [newHomeworkContent(defaultEditingDayByRepeatBy(repeatBy))]
}

function inferRepeatByFromTempalte(template: HomeworkTemplate): HomeworkRepeatBy {
  if (template.repeat_type) {
    return template.repeat_type
  }

  if (template.time_scope_category === 'weekend') {
    return 'weekend'
  }

  return inferRepeatBy(template.homework_contents)
}

export function validateHomeworkIntelligentRepeat(form: IHomeworkIntelligentRule) {
  if (!form.repeatBy) {
    throw new Error('请选择布置方式')
  }
}

export function validateHomeworkIntelligentTrainingDuration(form: IHomeworkIntelligentRule) {
  if (!form.training_duration) {
    throw new Error('请选择训练时长')
  }
}

export function validateHomeworkIntelligentPurpose(form: IHomeworkIntelligentRule) {
  if (!form.purpose) {
    throw new Error('请选择训练目标')
  }
}
export function validateHomeworkIntelligentContainerTypes(form: IHomeworkIntelligentRule) {
  if (!form.container_types.length) {
    throw new Error('请选择训练练习项目')
  }
}

export function validateHomeworkIntelligentCoreTrainingAspectNames(form: IHomeworkIntelligentRule) {
  if (form.core_training_aspect_names.length < 3) {
    throw new Error('请至少选择三个核心项目')
  }
}

export function prepareEdit(plan: HomeworkPlan) {
  const {
    clazz,
    id,
    client_status_type,
    homework_contents: homeworkContents,

    group: {
      group_id: groupId,
      start_time: startDate,
      expire_time: endDate,
      is_check_wd5: fridayChecked,
      release_type: releaseType,
      messages,
    },
  } = plan
  setEditingForm({
    ...initForm(),
    groupId,
    client_status_type,
    schedule_id: id,
    classes: [clazz],
    homeworkContents: cloneDeep(homeworkContents),
    startDate,
    endDate,
    repeatBy: calcRepeatBy(plan),
    fridayChecked,
    message: homeworkPlanService.formatMessages(messages),
    releaseType,
  })
}

export async function prepareCreate(clazzId?: number, typ?: number) {
  // let ownClasses = await fetchOwnClasses()
  let ownClasses = await AClazz.classesSportMine(typ ? { typ } : void 0)
  if (clazzId) {
    ownClasses = filterOwnClassesByClazzIds(ownClasses, [clazzId]) as Clazz[]
  }

  setEditingForm({
    ...initForm(),
    classes: ownClasses,
    homeworkContents: [{ homework_items: await buildDefaultHomeworkItems(ownClasses) }],
  })
}

export async function prepareCreateFromTemplate(template: HomeworkTemplate, clazzId?: number, typ?: number) {
  // const ownClasses = await fetchOwnClasses()
  const ownClasses = await AClazz.classesSportMine(typ ? { typ } : void 0)
  const classes = filterOwnClassesByClazzIds(ownClasses, clazzId ? [clazzId] : template.class_ids || [])
  const endDate = getRecentExpireTimeByClassList(classes as Clazz[])

  setEditingForm({
    ...initForm(),
    classes,
    startDate: template.start_date,
    endDate: endDate ? DateUtil.addSeconds(new Date(endDate), -1).toISOString() : null,
    repeatBy: inferRepeatByFromTempalte(template),
    homeworkContents: buildHomeworkContentsFromTemplate(template.homework_contents),
    message: template.message,
    fridayChecked: template.is_check_wd5,
    releaseType: template.release_type,
    template_id: template.template_id,
    is_modify: false,
    hw_tpl_id: template.hw_tpl_id,
    title: template.title,
  })
}

export async function prepareCreateFromTemplateForNoLogin(template: HomeworkTemplate) {
  setEditingForm({
    ...initForm(),
    classes: [],
    startDate: template.start_date,
    repeatBy: inferRepeatByFromTempalte(template),
    homeworkContents: buildHomeworkContentsFromTemplate(template.homework_contents),
    message: template.message,
    fridayChecked: template.is_check_wd5,
    releaseType: template.release_type,
    template_id: template.template_id,
    is_modify: false,
    hw_tpl_id: template.hw_tpl_id,
    title: template.title,
  })
}

export function adjustRestOptions(form: HomeworkPlanForm) {
  const options = [15, 30, 60, 120]
  form?.homeworkContents.forEach((c) => {
    c.homework_items.forEach((drill) => {
      if (drill.container_type === 'rest' && !options.includes(drill.repeats)) {
        const differences = options.map((option) => Math.abs(drill.repeats - option))
        const minValue = Math.min(...differences)
        const minIndex = differences.indexOf(minValue)
        drill.repeats = options[minIndex]
      }
    })
  })
}

// 检查选中的班级中是否有生效中任务的班级
export async function checkClasslistInProcessHomework(
  form: HomeworkPlanForm | IHomeworkIntelligentRule,
  ids: number[]
) {
  if (!isToday(new Date(form?.startDate as string))) {
    return []
  }

  const homeworkList = await AHomeworkNew.teacherHomeworkList(ids)
  const hasWorkClassList = homeworkList.filter((item) => item.homework?.client_status_type === 1)

  return hasWorkClassList.map((item) => item.clazz.note_name || item.clazz.name)
}
